import { AgendamentoCidadaoSection } from '@agenda/cidadao/list/AgendamentoCidadaoSection'
import { css } from '@emotion/react'
import { VFlow } from 'bold-ui'
import { Divider } from 'components/divider/Divider'
import { startOfDay } from 'date-fns'
import { useAgendamentosCidadaoPagedQuery } from 'graphql/hooks.generated'
import { SituacaoAgendadoEnum, TipoAcompanhamentoEnum } from 'graphql/types.generated'
import { useServerTime } from 'hooks/useServerTime'
import { Fragment, useMemo } from 'react'
import { GRUPO_CBO_ACOMP_PRE_NATAL_E_PUERPERIO, isCboDentista } from 'types/NivelCbo'
import { AgendamentoCidadaoModel } from 'view/agenda/cidadao/model-cidadao'

import { AcompanhamentoCard } from '../../components/AcompanhamentoCard'
import { AcompanhamentoHeaderLink } from '../../components/AcompanhamentoHeaderLink'

interface ProximosAgendamentosCardProps {
  cidadaoId: ID
  tipoAcompanhamento: TipoAcompanhamentoEnum
  agendamentosPath: string
}

export function ProximosAgendamentosCard(props: Readonly<ProximosAgendamentosCardProps>) {
  const { cidadaoId, tipoAcompanhamento, agendamentosPath } = props
  const { getServerTimeNow } = useServerTime()
  const now = getServerTimeNow()
  const today = startOfDay(now)

  const { data, loading } = useAgendamentosCidadaoPagedQuery({
    variables: {
      input: {
        cidadaoId: cidadaoId,
        dataAgendadoInicio: today,
        dataAgendadoFim: null,
        situacao: [SituacaoAgendadoEnum.AGENDADO],
        maisNovosPrimeiro: false,
        cbosToFilter: [...GRUPO_CBO_ACOMP_PRE_NATAL_E_PUERPERIO],
      },
    },
  })

  const agendamentos = useMemo(() => data?.agendados?.content, [data])

  const agendamentosFiltrados = useMemo(() => {
    const agendamentoDentista = agendamentos?.find((valor) => isCboDentista(valor.lotacaoAgendada?.cbo?.cbo2002))

    const agendamentosMedicoEnfermeiro = agendamentos?.filter(
      (valor) => !isCboDentista(valor.lotacaoAgendada?.cbo?.cbo2002)
    )

    if (agendamentosMedicoEnfermeiro?.length) {
      return agendamentoDentista
        ? [agendamentosMedicoEnfermeiro[0], agendamentoDentista]
        : agendamentosMedicoEnfermeiro.slice(0, 2)
    }

    return agendamentoDentista ? [agendamentoDentista] : []
  }, [agendamentos])

  const renderAgendamentoRow = (index: number, agendamento: AgendamentoCidadaoModel) => {
    return (
      <Fragment>
        {!!index && <Divider style={styles.divider} />}
        <AgendamentoCidadaoSection agendamento={agendamento} isAgendaOnlineAtiva={data?.conexao.agendaOnline.ativa} />
      </Fragment>
    )
  }

  const hasAgendamentos = agendamentos?.length > 0

  return (
    <AcompanhamentoCard
      id='proximos-agendamentos'
      title='Próximo(s) Agendamento(s)'
      tipoAcompanhamento={tipoAcompanhamento}
      loading={loading}
      placeholder='Nenhum agendamento para enfermeiro/médico/dentista registrado'
      headerContent={<AcompanhamentoHeaderLink title='Visualizar todos' path={agendamentosPath} />}
      justifyHeaderContent='space-between'
      bodyPadding={hasAgendamentos ? '0.5rem 0' : undefined}
    >
      {hasAgendamentos && (
        <VFlow vSpacing={0}>
          {agendamentosFiltrados.map((agendamento: AgendamentoCidadaoModel, index) =>
            renderAgendamentoRow(index, agendamento)
          )}
        </VFlow>
      )}
    </AcompanhamentoCard>
  )
}

const styles = {
  divider: css`
    margin: 0.5rem 0;
  `,
}
